import { useState, useEffect } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { categories, knowledgeBaseContent, type KBSection } from "./knowledge-base-content";

interface TableOfContentsProps {
  activeSection: string;
  onSectionClick: (sectionId: string) => void;
  filteredSections?: string[]; // IDs of sections that match search
}

export function TableOfContents({
  activeSection,
  onSectionClick,
  filteredSections,
}: TableOfContentsProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(categories.map((c) => c.id))
  );

  // Auto-expand category when active section changes
  useEffect(() => {
    const section = knowledgeBaseContent.find((s) => s.id === activeSection);
    if (section) {
      setExpandedCategories((prev) => {
        const next = new Set(prev);
        next.add(section.category);
        return next;
      });
    }
  }, [activeSection]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const getSectionsForCategory = (categoryId: string): KBSection[] => {
    const category = categories.find((c) => c.id === categoryId);
    if (!category) return [];
    return category.sections
      .map((sectionId) => knowledgeBaseContent.find((s) => s.id === sectionId))
      .filter((s): s is KBSection => s !== undefined);
  };

  const isSectionVisible = (sectionId: string): boolean => {
    if (!filteredSections) return true;
    return filteredSections.includes(sectionId);
  };

  const isCategoryVisible = (categoryId: string): boolean => {
    if (!filteredSections) return true;
    const sections = getSectionsForCategory(categoryId);
    return sections.some((s) => filteredSections.includes(s.id));
  };

  return (
    <nav className="space-y-1">
      {categories.map((category) => {
        if (!isCategoryVisible(category.id)) return null;

        const sections = getSectionsForCategory(category.id);
        const isExpanded = expandedCategories.has(category.id);
        const hasActiveSection = sections.some((s) => s.id === activeSection);

        return (
          <div key={category.id} className="space-y-1">
            <button
              onClick={() => toggleCategory(category.id)}
              className={cn(
                "flex items-center gap-2 w-full px-2 py-1.5 text-sm font-medium rounded-md transition-colors",
                "hover:bg-muted text-left",
                hasActiveSection && "text-primary"
              )}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 flex-shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 flex-shrink-0" />
              )}
              {category.title}
            </button>

            {isExpanded && (
              <div className="ml-4 space-y-0.5 border-l pl-2">
                {sections.map((section) => {
                  if (!isSectionVisible(section.id)) return null;

                  const Icon = section.icon;
                  const isActive = section.id === activeSection;

                  return (
                    <button
                      key={section.id}
                      onClick={() => onSectionClick(section.id)}
                      className={cn(
                        "flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-md transition-colors text-left",
                        "hover:bg-muted",
                        isActive
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-muted-foreground"
                      )}
                    >
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{section.title}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}
